import type { TypingSocketEvent } from '@tandemone/types'
import { useEffect, useState } from 'react'

import { getSocket } from '@/api/socket'

const TYPING_TTL_MS = 5_000

type TypingEntry = {
  userId: string
  userName: string | null
  expiresAt: number
}

/** track who's currently typing in `conversationId`. listens for
 * `messaging:typing` socket events filtered to this conversation, holds
 * each (userId → entry) with a 5s TTL, and removes entries either when
 * a `isTyping: false` event lands or when the TTL expires (safety net
 * for sockets that drop without sending the stop event).
 *
 * returns a snapshot of currently-typing users. the sender never sees
 * themselves because the server uses `socket.to(room).emit(...)` which
 * excludes the originating socket . no client-side self-filter needed.
 */
export function useTypingPresence(
  conversationId: string | undefined
): Array<{ userId: string; userName: string | null }> {
  const [entries, setEntries] = useState<Map<string, TypingEntry>>(new Map())

  useEffect(() => {
    if (!conversationId) return
    const socket = getSocket()

    function onTyping(event: TypingSocketEvent): void {
      if (event.conversationId !== conversationId) return
      setEntries(prev => {
        const next = new Map(prev)
        if (event.isTyping) {
          next.set(event.userId, {
            userId: event.userId,
            userName: event.userName,
            expiresAt: Date.now() + TYPING_TTL_MS
          })
        } else {
          next.delete(event.userId)
        }
        return next
      })
    }

    socket.on('messaging:typing', onTyping)
    /** sweep expired entries every second. cheap interval; only runs
     * while a thread is mounted.
     */
    const sweep = window.setInterval(() => {
      setEntries(prev => {
        const now = Date.now()
        let changed = false
        const next = new Map(prev)
        for (const [userId, entry] of next) {
          if (entry.expiresAt <= now) {
            next.delete(userId)
            changed = true
          }
        }
        return changed ? next : prev
      })
    }, 1_000)

    return () => {
      socket.off('messaging:typing', onTyping)
      window.clearInterval(sweep)
      setEntries(new Map())
    }
  }, [conversationId])

  return Array.from(entries.values()).map(entry => ({
    userId: entry.userId,
    userName: entry.userName
  }))
}
